import { useState } from 'preact/hooks';
import { addPasskey, api, del, get, media, patch, post, put } from '../api';
import { me, refreshMe } from '../session';
import { startCheckout } from '../billing';
import { Button, Card, Empty, Field, Modal, money, Pill, timeAgo, toast, useAction, useLoad } from '../ui';

export function Account() {
	const m = me.use();
	return (
		<main class="page">
			<h1>Account</h1>
			<div class="two-col">
				<Profile name={m?.user?.displayName ?? ''} />
				<Passkeys recoveryLeft={m?.recoveryCodesLeft ?? 0} />
			</div>
			<Plans />
			<div class="two-col">
				<Keys />
				<Earnings />
			</div>
			<AssetLibrary />
		</main>
	);
}

function Profile(p: { name: string }) {
	const [name, setName] = useState(p.name);
	const [save, busy] = useAction(async () => {
		await patch('/api/me', { displayName: name });
		await refreshMe();
		toast('Name saved.', 'ok');
	});
	return (
		<Card title="Profile">
			<Field label="Display name" hint="Shown to other players. Names don't have to be unique.">
				<input value={name} onInput={(e) => setName(e.currentTarget.value)} maxLength={40} />
			</Field>
			<Button onClick={() => save()} busy={busy}>
				Save
			</Button>
		</Card>
	);
}

function Passkeys(p: { recoveryLeft: number }) {
	const list = useLoad(() => get('/api/me/passkeys'));
	const [codes, setCodes] = useState<string[] | null>(null);
	const [add, adding] = useAction(async () => {
		await addPasskey();
		toast('Passkey added.', 'ok');
		list.reload();
	});
	const [gen, generating] = useAction(async () => {
		const r = await post('/api/me/recovery-codes');
		setCodes(r.codes);
		await refreshMe();
	});
	return (
		<Card title="Passkeys" actions={<Button small onClick={() => add()} busy={adding}>Add a passkey</Button>}>
			<ul class="list">
				{(list.data?.passkeys ?? []).map((k: any) => (
					<li key={k.id} class="row-between">
						<span>
							{k.name} <span class="muted small">added {timeAgo(k.created_at)}{k.last_used_at ? `, used ${timeAgo(k.last_used_at)}` : ''}</span>
						</span>
						<Button
							small
							variant="quiet"
							onClick={async () => {
								try {
									await del(`/api/me/passkeys/${encodeURIComponent(k.id)}`);
									list.reload();
								} catch (e) {
									toast(String((e as Error).message), 'error');
								}
							}}
						>
							Remove
						</Button>
					</li>
				))}
			</ul>
			<p class="small">
				Recovery codes left: <strong>{p.recoveryLeft}</strong>. Keep them somewhere safe; each signs you in once if you lose every passkey.
			</p>
			<Button variant="ghost" small onClick={() => gen()} busy={generating}>
				{p.recoveryLeft ? 'Replace recovery codes' : 'Generate recovery codes'}
			</Button>
			{codes ? (
				<Modal title="Your recovery codes" onClose={() => setCodes(null)}>
					<p>Save these now — they won't be shown again.</p>
					<pre class="codes">{codes.join('\n')}</pre>
					<Button onClick={() => navigator.clipboard?.writeText(codes.join('\n')).then(() => toast('Copied.', 'ok'))}>Copy</Button>
				</Modal>
			) : null}
		</Card>
	);
}

function Keys() {
	const list = useLoad(() => get('/api/me/keys'));
	const [editing, setEditing] = useState<string | null>(null);
	const [value, setValue] = useState('');
	const [save, busy] = useAction(async () => {
		await put(`/api/me/keys/${editing}`, { apiKey: value });
		setEditing(null);
		setValue('');
		list.reload();
		toast('Key saved. It is stored encrypted and never shown again.', 'ok');
	});
	const label: Record<string, string> = { openai: 'OpenAI', openrouter: 'OpenRouter', xai: 'xAI (Grok)', anthropic: 'Anthropic' };
	return (
		<Card title="Your model keys (BYOK)">
			<p class="small muted">Used only by games you run, decrypted only for each call. Keys are never shown again after saving.</p>
			<ul class="list">
				{(list.data?.keys ?? []).map((k: any) => (
					<li key={k.provider} class="row-between">
						<span>
							{label[k.provider]} {k.set ? <Pill tone="ok">saved</Pill> : <Pill>not set</Pill>}
						</span>
						<span class="row">
							<Button small variant="ghost" onClick={() => setEditing(k.provider)}>
								{k.set ? 'Replace' : 'Add'}
							</Button>
							{k.set ? (
								<Button
									small
									variant="quiet"
									onClick={async () => {
										await del(`/api/me/keys/${k.provider}`);
										list.reload();
									}}
								>
									Delete
								</Button>
							) : null}
						</span>
					</li>
				))}
			</ul>
			{editing ? (
				<Modal title={`${label[editing]} key`} onClose={() => setEditing(null)}>
					<Field label="API key">
						<input type="password" value={value} onInput={(e) => setValue(e.currentTarget.value)} autocomplete="off" />
					</Field>
					<Button onClick={() => save()} busy={busy} disabled={value.length < 10}>
						Save key
					</Button>
				</Modal>
			) : null}
		</Card>
	);
}

function Plans() {
	const status = useLoad(() => get('/api/billing/status'));
	const plans = useLoad(() => get('/api/billing/plans'));
	const [buy, busy] = useAction(async (body: any) => {
		if (await startCheckout(body)) status.reload();
	});
	const [cancel] = useAction(async () => {
		await post('/api/billing/cancel');
		status.reload();
		toast('Your plan will drop to Free at the end of this period.', 'ok');
	});
	const s = status.data?.plan;
	return (
		<Card title="Plan and billing">
			{s ? (
				<p>
					You're on <strong>{s.def.name}</strong>
					{s.status === 'cancelled' ? ' (cancelled — ends at period end)' : s.status === 'lapsed' ? ' (lapsed: a renewal payment did not arrive)' : ''}.{' '}
					{s.allowance ? (
						<>
							Beats this month: {Math.round(s.beatsUsed)} of {s.allowance}
							{s.topup ? ` + ${Math.round(s.topup)} top-up` : ''}.{' '}
						</>
					) : null}
					{s.jevRemaining !== null ? `Jev decisions left: ${Math.round(s.jevRemaining)} beats. ` : ''}
					Renews {new Date(s.periodEnd).toLocaleDateString()}.
				</p>
			) : null}
			<div class="plans">
				{(plans.data?.plans ?? []).map((p: any) => (
					<div key={p.id} class={`plan ${s?.plan === p.id ? 'plan-on' : ''}`}>
						<h3>{p.name}</h3>
						<p class="price">{p.priceCents ? `${money(p.priceCents)}/mo` : 'Free'}</p>
						{p.priceCents ? <p class="small muted">{status.data?.currency ?? 'USD'}, renews automatically; cancel any time</p> : null}
						<p class="small">{p.blurb}</p>
						{p.id !== 'free' && s?.plan !== p.id ? (
							<div class="row">
								<Button small onClick={() => buy({ kind: 'plan', plan: p.id })} busy={busy}>
									Monthly
								</Button>
								<Button small variant="ghost" onClick={() => buy({ kind: 'plan', plan: p.id, interval: 'year' })} busy={busy}>
									Yearly ({money(p.priceCents * 10)})
								</Button>
							</div>
						) : null}
					</div>
				))}
			</div>
			<div class="row">
				{s?.def?.topupCents ? (
					<Button variant="ghost" onClick={() => buy({ kind: 'topup' })} busy={busy}>
						Top up 1,000 beats ({money(s.def.topupCents)})
					</Button>
				) : null}
				{s && s.plan !== 'free' && s.status !== 'cancelled' ? (
					<Button variant="quiet" onClick={() => cancel()}>
						Cancel plan
					</Button>
				) : null}
			</div>
			{status.data?.purchases?.length ? (
				<>
					<h3>Purchases</h3>
					<ul class="list">
						{status.data.purchases.map((p: any) => (
							<li key={p.id} class="row-between">
								<span>
									{p.kind === 'template' ? p.title : p.kind} · {money(p.amount_cents)} · {timeAgo(p.created_at)} {p.status === 'refunded' ? <Pill tone="warn">refunded</Pill> : null}
								</span>
								{p.kind === 'template' && p.status === 'paid' && !p.campaign_started && Date.now() - p.created_at < 14 * 86400_000 ? (
									<Button
										small
										variant="quiet"
										onClick={async () => {
											try {
												await post(`/api/purchases/${p.id}/refund`);
												status.reload();
												toast('Refunded.', 'ok');
											} catch (e) {
												toast((e as Error).message, 'error');
											}
										}}
									>
										Refund
									</Button>
								) : null}
							</li>
						))}
					</ul>
				</>
			) : null}
		</Card>
	);
}

function Earnings() {
	const e = useLoad(() => get('/api/billing/earnings'));
	if (!e.data) return null;
	return (
		<Card title="Template earnings">
			<p>
				Pending: <strong>{money(e.data.pendingCents)}</strong>. Payouts are monthly once your balance passes {money(e.data.thresholdCents)} (75% to you after processing fees).
			</p>
			{e.data.payouts.length ? (
				<ul class="list">
					{e.data.payouts.map((p: any) => (
						<li key={p.id}>
							{p.period}: {money(p.amount_cents)} <Pill>{p.status}</Pill>
						</li>
					))}
				</ul>
			) : (
				<Empty>No payouts yet.</Empty>
			)}
		</Card>
	);
}

export function AssetLibrary(p: { pick?: (id: string) => void; kind?: string }) {
	const list = useLoad(() => get('/api/assets'));
	const [upload, busy] = useAction(async (file: File) => {
		const fd = new FormData();
		fd.set('file', file);
		fd.set('name', file.name);
		fd.set('kind', p.kind ?? 'image');
		await api('POST', '/api/assets', fd);
		list.reload();
	});
	return (
		<Card title="Art library" actions={<label class="btn btn-ghost btn-sm">{busy ? 'Uploading…' : 'Upload image'}<input type="file" accept="image/*" hidden onChange={(e) => e.currentTarget.files?.[0] && upload(e.currentTarget.files[0])} /></label>}>
			{list.data?.assets?.length ? (
				<div class="asset-grid">
					{list.data.assets.map((a: any) => (
						<figure key={a.id} class="asset" draggable onDragStart={(e) => e.dataTransfer?.setData('text/asset', a.id)}>
							<img src={media(a.id, 320)} alt={a.name} loading="lazy" />
							<figcaption>
								<span class="clamp">{a.name}</span>
								{p.pick ? (
									<Button small onClick={() => p.pick!(a.id)}>
										Use
									</Button>
								) : (
									<Button
										small
										variant="quiet"
										onClick={async () => {
											await del(`/api/assets/${a.id}`);
											list.reload();
										}}
									>
										Delete
									</Button>
								)}
							</figcaption>
						</figure>
					))}
				</div>
			) : (
				<Empty>No images yet. Upload area backgrounds, portraits, ending screens and covers.</Empty>
			)}
		</Card>
	);
}
